import React from 'react';
import styled from 'styled-components';
import { ReactComponent as DisabledIcon } from '@elementary/common-ui/assets/16x16/disabled.svg';
import { ReactComponent as JiraIcon } from '@elementary/common-ui/assets/40x40/jira.svg';
import { ReactComponent as LinearIcon } from '@elementary/common-ui/assets/40x40/linear.svg';
import { ReactComponent as ServiceNowIcon } from '@elementary/common-ui/assets/40x40/servicenow.svg';
import { ReactComponent as Logo } from '@elementary/common-ui/assets/logo.svg';
import { ReactComponent as DeleteConnection } from '@elementary/common-ui/assets/minus-x-minus.svg';
import { Dialog, FlexCenter } from '@elementary/common-ui/components';
import { TicketingIntegration } from 'shared/types';
import { Typography } from '@mui/material';

const platformIconMap: Record<string, React.ReactNode> = {
  jira: <JiraIcon />,
  linear: <LinearIcon />,
  servicenow: <ServiceNowIcon />,
};

const IconsWrapper = styled(FlexCenter)`
  gap: 8px;
  height: 100%;
`;

const LogoWrapper = styled(FlexCenter)`
  width: 72px;
  height: 72px;
  border-radius: 12px;
  padding: 16px;
  background: ${({ theme }) => theme.palette.common.magenta};
`;

const PlatformIconWrapper = styled(FlexCenter)`
  border-radius: 12px;
  padding: 16px;
  border: 2px solid ${({ theme }) => theme.palette.common.gray5};
  box-sizing: border-box;
`;

const TopPartGraphic = ({
  selectedIntegration,
}: {
  selectedIntegration: TicketingIntegration | undefined;
}) => (
  <IconsWrapper>
    <LogoWrapper>
      <Logo />
    </LogoWrapper>

    <DeleteConnection />

    <PlatformIconWrapper>
      {selectedIntegration?.type && platformIconMap[selectedIntegration.type]}
    </PlatformIconWrapper>
  </IconsWrapper>
);

type Props = {
  open: boolean;
  onClose: () => void;
  onSubmit: (id: string) => void;
  loading?: boolean;
  selectedIntegration?: TicketingIntegration;
};

const DeleteTicketingToolDialog = ({
  open,
  onClose,
  selectedIntegration,
  onSubmit,
  loading,
}: Props) => (
  <Dialog
    open={open}
    graphic={TopPartGraphic({ selectedIntegration })}
    titleIcon={<DisabledIcon />}
    title={'Delete connection'}
    message={
      <Typography>
        Are you sure you want to delete your{' '}
        <Typography component="span" sx={{ mi: 0.5, fontWeight: 700 }}>
          {selectedIntegration?.name}
        </Typography>{' '}
        ticketing connection?
      </Typography>
    }
    secondaryMessage={`Deleting this connection will remove your "${selectedIntegration?.name}" ticketing integration from Elementary. This action is irreversible.`}
    onClose={onClose}
    onConfirmButtonClick={() =>
      selectedIntegration && onSubmit(selectedIntegration.id)
    }
    isLoading={!!loading}
    confirmText={loading ? 'Deleting' : 'Delete connection'}
    variant="error"
  />
);

export default DeleteTicketingToolDialog;
